import React, { useState, useEffect, useRef } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import { BarPlot } from '@mui/x-charts/BarChart';
import { LinePlot } from '@mui/x-charts/LineChart';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsLegend } from '@mui/x-charts/ChartsLegend';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';


const findDataRange = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return { start: 0, end: 0 };

    let startIdx = 32; // Start 8 hours in.
    let endIdx = dataArray.length - 1;

    // Find first non-zero/non-null value
    while (startIdx < dataArray.length &&
    (dataArray[startIdx] === 0 || dataArray[startIdx] === null || isNaN(dataArray[startIdx]))) {
        startIdx++;
    }

    // Find last non-zero/non-null value
    while (endIdx >= 0 &&
    (dataArray[endIdx] === 0 || dataArray[endIdx] === null || isNaN(dataArray[endIdx]))) {
        endIdx--;
    }

    return { start: startIdx, end: endIdx };
};


// This component produces the graph for subcatchments, where you have both runoff and rainfall to plot
function SubcatchChartResults({ x, y1, y2, y3}) {

    const [showLine, setShowLine] = useState(true);
    const [showBar, setShowBar] = useState(true);

    const firstTimestamp = new Date(x[0]);
    const xAxisData = x.map(timestamp => ((new Date(timestamp) - firstTimestamp) / 3600000)); // Convert timestamps to hours

    // Get ranges for each data series
    //const y1Range = findDataRange(y1); // Filter this only based on runoff
    const y2Range = findDataRange(y2);
    const y3Range = findDataRange(y3);
    // Get overall time range
    const minStart = Math.min(y2Range.start, y3Range.start);
    const maxEnd = Math.max(y2Range.end, y3Range.end);

    // Add 2 hours to pad the time range
    if (!maxEnd) return;
    const filteredIndices = [...Array(maxEnd + 5 - minStart).keys()].map(i => i + minStart);


    // Create filtered arrays for our dataset
    const filteredXAxisData = filteredIndices.map(i => xAxisData[i]);
    const filteredY1 = filteredIndices.map(i => y1[i]);
    const filteredY2 = filteredIndices.map(i => y2[i]);
    const filteredY3 = filteredIndices.map(i => y3[i]);


    const dataset = filteredXAxisData.map((time, index) => ({
        time,
        og_runoff: filteredY1[index],
        rainfall: filteredY2[index],
        new_runoff: filteredY3[index]
    }));

    const series = [
        { type: 'line', dataKey: 'og_runoff', color: '#00afff', label: 'Baseline Runoff' },
        { type: 'line', dataKey: 'new_runoff', color: '#ffc800', label: 'Your Plan' },
        { type: 'bar', dataKey: 'rainfall', color: 'rgba(255, 255, 255, 0.5)', yAxisId: 'rightAxis', label: 'Rainfall'}
    ];

    return (
        <Box sx={{ width: '100%', height: '500px', alignItems: 'flex-start', overflow: 'hidden', padding: '0px 0px 0px 0px' }}>
            <Box sx={{ marginBottom: '0px', 
                    marginLeft: '20px', 
                    marginTop: '0px',
                    display: 'flex',
                    alignItems: 'center',
                    '& > *': { marginRight: '8px' }
            }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showLine}
                            onChange={(e) => setShowLine(e.target.checked)}
                        />
                    }
                    label="Runoff"
                    sx={{ marginBottom: '0px', marginTop: '0px' }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showBar}
                            onChange={(e) => setShowBar(e.target.checked)}
                        />
                    }
                    label="Rainfall"
                    sx={{ marginBottom: '0px', marginTop: '0px', marginRight: '2px' }}
                />

            </Box>
            <Box sx={{ marginBottom: '0px', marginLeft: '0px', marginTop: '0px' }}>
                <ResponsiveChartContainer
                sx={{
                    //change axis label and line colors
                    "& .MuiChartsAxis-tickContainer .MuiChartsAxis-tickLabel":{
                        fontFamily: "Roboto",
                        },
                    "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel":{
                        fill:"#26a6e0"
                    },
                    "& .MuiChartsAxis-left .MuiChartsAxis-line":{
                        stroke:"#26a6e0",
                        },
                    "& .MuiChartsAxis-left .MuiChartsAxis-tick":{
                        stroke:"#26a6e0",
                        },
                    "& .MuiChartsAxis-left .MuiChartsAxis-label":{
                        fill:"#26a6e0",
                        },
                    "& .MuiChartsAxis-right .MuiChartsAxis-line":{
                        stroke:"rgba(255, 255, 255, 0.5)",
                        },
                    "& .MuiChartsAxis-right .MuiChartsAxis-tick":{
                        stroke:"rgba(255, 255, 255, 0.5)",
                        },
                    "& .MuiChartsAxis-right .MuiChartsAxis-label":{
                        fill:"rgba(255, 255, 255, 0.5)",
                        },
                    "& .MuiChartsAxis-right .MuiChartsAxis-tickLabel":{
                        fill:"rgba(255, 255, 255, 0.5)"
                    },
                }}
                series={series}
                xAxis={[
                    {
                    scaleType: 'band',
                    dataKey: 'time',
                    label: 'Time (hours)',
                    tickInterval: (value, index) => index % 24 === 0,
                    }
                ]}
                yAxis={[
                    { id: 'leftAxis', label: 'Runoff (cfs)', scaleType: 'sqrt'},
                    { id: 'rightAxis', position: 'right', label: 'Rainfall (in)', scaleType: 'sqrt'}
                ]}
                dataset={dataset}
                height={280}
                >
                {showBar && <BarPlot />}
                {showLine && <LinePlot />}
                <ChartsXAxis />
                <ChartsYAxis axisId="leftAxis" />
                <ChartsYAxis axisId="rightAxis" position="right" />
                <ChartsTooltip/>
                <ChartsLegend />
                </ResponsiveChartContainer>
            </Box>
        </Box>
    );
}

export default SubcatchChartResults;